import { type FC, useMemo } from 'react'
import cn from 'classnames'
import { dateFormat, Currency } from '@egw/utils'
import { useTranslation } from '@egw/i18n'
import { type BankActivity, TransactionMethod } from '@egw/api'
import { getPendingStatus, PendingStatusType } from '../../utils'

export interface TransactionItemProps {
  transaction: BankActivity
  onClick: () => void
}

export const TransactionItem: FC<TransactionItemProps> = ({ transaction, onClick }) => {
  const { t } = useTranslation('features-transactions')

  const pendingStatus = getPendingStatus(transaction)

  const transactionDate = new Date(transaction.date)
  const amount = transaction.amount.amount

  const amountStr = useMemo(() => {
    return Currency.integerCents.plusMinus(amount)
  }, [amount])

  const displayNameClasses = cn('typo-primary-base-regular self-start block text-text-body truncate mr-1 md:mr-0', {
    'col-span-9': amountStr.length <= 5,
    'col-span-8': amountStr.length > 5 && amountStr.length <= 10,
    'col-span-7': amountStr.length > 10,
  })
  const amountClasses = cn('typo-primary-base-semibold text-text-body', {
    'text-body': !transaction.isProcessing,
    'text-meta': transaction.isProcessing,
    'col-span-4': amountStr.length > 10,
    'col-span-3': amountStr.length > 5 && amountStr.length <= 10,
    'col-span-2': amountStr.length <= 5,
  })
  const labelClasses = cn('typo-primary-sm-regular text-text-meta text-left flex pl-1 capitalize mt-3xs', {
    'col-span-9': amountStr.length <= 5,
    'col-span-8': amountStr.length > 5 && amountStr.length <= 10,
    'col-span-7': amountStr.length > 10,
  })
  const statusClasses = cn('text-green-500 typo-primary-sm-semibold mt-3xs', {
    'text-alertWarningTitle': pendingStatus?.type === PendingStatusType.WARNING,
    'text-alertSuccessTitle': pendingStatus?.type === PendingStatusType.SUCCESS,
    'col-span-4': amountStr.length > 10,
    'col-span-3': amountStr.length > 5 && amountStr.length <= 10,
    'col-span-2': amountStr.length <= 5,
  })

  return (
    <div className="border-light border-b last-of-type:border-none">
      <button id={transaction.activityId} type="button" onClick={onClick} className="w-full transaction_link">
        <div className="w-full py-4 gap-1 grid grid-cols-12 flex-row justify-center">
          <div className="flex flex-row col-span-1 min-w-0 items-center">
            {/* Date Column */}
            <div className="flex-col text-text-meta flex self-start text-left">
              {/* Day */}
              <p className="typo-primary-lg-regular">
                {transaction.date ? dateFormat(new Date(transactionDate), t('date.day')) : null}
              </p>
              {/* Month */}
              <p className="typo-primary-xs-bold text-center">
                {transaction.date ? dateFormat(new Date(transactionDate), t('date.month')).toUpperCase() : null}
              </p>
            </div>
          </div>

          {/* Top Row */}
          <div className="flex flex-row col-span-11 col-start-2 pl-1 justify-between truncate relative top-[1px]">
            {/* Display Name */}
            <p className={displayNameClasses}>{transaction.displayName}</p>
            {/* Amount */}
            <p className={amountClasses}>{amountStr}</p>
          </div>

          {/* Bottom Row */}
          <div className="flex flex-row text-right self-start col-span-11 col-start-2 justify-between relative top-[-1.3rem] mb-[-1.3rem]">
            {/* Label */}
            <p className={labelClasses}>{transaction.label}</p>
            {/* Status */}
            {pendingStatus && transaction.method === TransactionMethod.ACH ? (
              <p className={statusClasses}>{t(`activity.pending_banner.${pendingStatus.content}.title`)}</p>
            ) : null}
          </div>
        </div>
      </button>
    </div>
  )
}
